// ═══════════════════════════════════════════════════════════════
// StyleNotesPanel — Drawer latéral droit annotations stylistiques
// S58 P3 — Module COM/Écrivain (annotations typées par chapitre)
// ═══════════════════════════════════════════════════════════════
//
// Drawer 340px à droite de l'atelier, toggle persisté localStorage.
// Filtres : Tous / Todo / Transition / Validé (date DESC dans chaque vue).
// Création inline (type + textarea), case resolved toggle, delete confirm.
// Icônes par type, couleurs cohérentes atelier.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useCallback } from 'react';
import {
    X, Plus, Zap, BarChart3, Clock, Type, ArrowLeftRight, Circle, Check, Trash2, Loader,
} from 'lucide-react';
import { listStyleNotes, createStyleNote, updateStyleNote, deleteStyleNote } from '../../services/bookApi';

const NOTE_TYPES = [
    { id: 'register',   label: 'Registre',    Icon: Zap,             color: '#f59e0b' },
    { id: 'density',    label: 'Densité',     Icon: BarChart3,       color: '#3b82f6' },
    { id: 'rhythm',     label: 'Rythme',      Icon: Clock,           color: '#8b5cf6' },
    { id: 'vocabulary', label: 'Vocabulaire', Icon: Type,            color: '#06b6d4' },
    { id: 'transition', label: 'Transition',  Icon: ArrowLeftRight,  color: '#a78bfa' },
    { id: 'todo',       label: 'À faire',     Icon: Circle,          color: '#ef4444' },
    { id: 'validated',  label: 'Validé',      Icon: Check,           color: '#10b981' },
];
const TYPE_MAP = Object.fromEntries(NOTE_TYPES.map(t => [t.id, t]));

const FILTERS = [
    { id: 'all',        label: 'Tous' },
    { id: 'todo',       label: 'Todo' },
    { id: 'transition', label: 'Transition' },
    { id: 'resolved',   label: 'Résolu' },
];

const StyleNotesPanel = ({ bookId, chapterId, onClose, onCountChanged }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [createType, setCreateType] = useState('register');
    const [createContent, setCreateContent] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        if (!bookId || !chapterId) return;
        setLoading(true);
        try {
            const res = await listStyleNotes(bookId, chapterId);
            setNotes(res?.notes || []);
            setError('');
            if (onCountChanged) onCountChanged((res?.notes || []).length);
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur chargement notes');
        } finally {
            setLoading(false);
        }
    }, [bookId, chapterId, onCountChanged]);

    useEffect(() => { load(); }, [load]);

    const filtered = notes.filter(n => {
        if (filter === 'all') return true;
        if (filter === 'resolved') return n.resolved;
        if (filter === 'todo') return n.note_type === 'todo' && !n.resolved;
        if (filter === 'transition') return n.note_type === 'transition';
        return true;
    });

    const handleCreate = async () => {
        if (!createContent.trim() || creating) return;
        setCreating(true);
        try {
            const res = await createStyleNote(bookId, chapterId, {
                noteType: createType, content: createContent.trim(),
            });
            if (res?.note) {
                setNotes(list => [res.note, ...list]);
                if (onCountChanged) onCountChanged(notes.length + 1);
            }
            setCreateContent('');
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur création note');
        } finally {
            setCreating(false);
        }
    };

    const handleToggleResolved = async (note) => {
        try {
            const res = await updateStyleNote(bookId, chapterId, note.id, { resolved: !note.resolved });
            if (res?.note) {
                setNotes(list => list.map(n => n.id === note.id ? res.note : n));
            }
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur mise à jour');
        }
    };

    const handleDelete = async (note) => {
        if (!window.confirm('Supprimer cette note ? (action irréversible)')) return;
        try {
            await deleteStyleNote(bookId, chapterId, note.id);
            setNotes(list => list.filter(n => n.id !== note.id));
            if (onCountChanged) onCountChanged(notes.length - 1);
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur suppression');
        }
    };

    return (
        <div style={{
            width: 300, flexShrink: 0, height: '100%',
            display: 'flex', flexDirection: 'column',
            background: 'var(--card-bg-secondary)',
            borderLeft: '1px solid var(--card-border)',
            minHeight: 0,
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--card-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 26, height: 26, borderRadius: 6,
                        background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Zap size={13} />
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                            Notes stylistiques
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                            {notes.length} {notes.length > 1 ? 'annotations' : 'annotation'}
                        </div>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    title="Fermer le panneau"
                    style={{
                        background: 'transparent', border: 'none',
                        color: 'var(--text-secondary)', cursor: 'pointer',
                        padding: 4,
                    }}
                >
                    <X size={16} />
                </button>
            </div>

            {/* Filtres */}
            <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid var(--card-border)',
                display: 'flex', gap: 4, flexWrap: 'wrap',
                flexShrink: 0,
            }}>
                {FILTERS.map(f => {
                    const active = filter === f.id;
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            style={{
                                padding: '4px 10px', borderRadius: 12, fontSize: 11,
                                background: active ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${active ? 'rgba(139,92,246,0.35)' : 'var(--card-border)'}`,
                                color: active ? '#c4b5fd' : 'var(--text-secondary)',
                                cursor: 'pointer',
                            }}
                        >
                            {f.label}
                        </button>
                    );
                })}
            </div>

            {/* Liste notes */}
            <div style={{ flex: 1, overflow: 'auto', padding: '10px 12px' }}>
                {loading && (
                    <div style={{
                        color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '20px 0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                        <Loader size={12} className="animate-spin" /> Chargement…
                    </div>
                )}

                {error && !loading && (
                    <div style={{
                        padding: '8px 10px', borderRadius: 6, marginBottom: 10,
                        background: 'rgba(239,68,68,0.08)',
                        border: '1px solid rgba(239,68,68,0.25)',
                        color: '#ef4444', fontSize: 11,
                    }}>
                        {error}
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div style={{
                        color: 'var(--text-muted)', fontSize: 11.5,
                        textAlign: 'center', padding: '30px 14px', lineHeight: 1.5,
                        fontStyle: 'italic',
                    }}>
                        {filter === 'all'
                            ? "Aucune annotation pour ce chapitre."
                            : `Aucune annotation dans le filtre « ${FILTERS.find(f => f.id === filter)?.label} ».`}
                    </div>
                )}

                {filtered.map(note => {
                    const meta = TYPE_MAP[note.note_type] || TYPE_MAP.todo;
                    const Icon = meta.Icon;
                    return (
                        <div
                            key={note.id}
                            style={{
                                padding: '10px 12px', marginBottom: 8, borderRadius: 8,
                                background: note.resolved ? 'rgba(16,185,129,0.05)' : 'rgba(255,255,255,0.03)',
                                border: `1px solid ${note.resolved ? 'rgba(16,185,129,0.2)' : 'var(--card-border)'}`,
                                opacity: note.resolved ? 0.72 : 1,
                            }}
                        >
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                marginBottom: 6,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Icon size={11} style={{ color: meta.color }} />
                                    <span style={{
                                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                        letterSpacing: 0.4, color: meta.color,
                                    }}>{meta.label}</span>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>·</span>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                        {formatDate(note.created_at)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: 2 }}>
                                    <button
                                        onClick={() => handleToggleResolved(note)}
                                        title={note.resolved ? 'Rouvrir' : 'Marquer résolu'}
                                        style={{
                                            padding: 3, borderRadius: 4,
                                            background: note.resolved ? 'rgba(16,185,129,0.15)' : 'transparent',
                                            border: 'none',
                                            color: note.resolved ? '#10b981' : 'var(--text-muted)',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Check size={11} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(note)}
                                        title="Supprimer"
                                        style={{
                                            padding: 3, borderRadius: 4,
                                            background: 'transparent', border: 'none',
                                            color: '#ef4444', cursor: 'pointer',
                                        }}
                                    >
                                        <Trash2 size={11} />
                                    </button>
                                </div>
                            </div>
                            <div style={{
                                fontSize: 12, color: 'var(--text)', lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                                textDecoration: note.resolved ? 'line-through' : 'none',
                                textDecorationColor: 'var(--text-muted)',
                            }}>
                                {note.content}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Création inline */}
            <div style={{
                padding: 12,
                borderTop: '1px solid var(--card-border)',
                background: 'var(--card-bg)',
                flexShrink: 0,
            }}>
                <div style={{
                    fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)',
                    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
                }}>
                    Nouvelle note
                </div>

                {/* Type picker compact */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
                    {NOTE_TYPES.map(t => {
                        const Icon = t.Icon;
                        const selected = createType === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setCreateType(t.id)}
                                title={t.label}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    padding: '3px 7px', borderRadius: 10, fontSize: 10,
                                    background: selected ? `${t.color}22` : 'rgba(255,255,255,0.03)',
                                    border: `1px solid ${selected ? `${t.color}66` : 'var(--card-border)'}`,
                                    color: selected ? t.color : 'var(--text-muted)',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon size={9} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                <textarea
                    value={createContent}
                    onChange={(e) => setCreateContent(e.target.value)}
                    placeholder="Décris l'annotation (ex: trop académique §3, ancrer charnellement)…"
                    rows={3}
                    maxLength={2000}
                    onKeyDown={(e) => {
                        if ((e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
                            e.preventDefault();
                            handleCreate();
                        }
                    }}
                    style={{
                        width: '100%', padding: '8px 10px', borderRadius: 6,
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid var(--card-border)',
                        color: 'var(--text)', fontSize: 12, lineHeight: 1.5,
                        resize: 'vertical', minHeight: 60,
                        boxSizing: 'border-box',
                    }}
                />
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginTop: 6,
                }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        Ctrl+Entrée pour ajouter
                    </div>
                    <button
                        onClick={handleCreate}
                        disabled={!createContent.trim() || creating}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                            background: 'rgba(139,92,246,0.18)',
                            border: '1px solid rgba(139,92,246,0.35)',
                            color: '#c4b5fd',
                            cursor: (!createContent.trim() || creating) ? 'not-allowed' : 'pointer',
                            opacity: (!createContent.trim() || creating) ? 0.5 : 1,
                        }}
                    >
                        {creating ? <Loader size={10} className="animate-spin" /> : <Plus size={11} />}
                        Ajouter
                    </button>
                </div>
            </div>
        </div>
    );
};

function formatDate(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60_000) return 'à l\'instant';
    if (diff < 3_600_000) return `il y a ${Math.floor(diff / 60_000)} min`;
    if (diff < 86_400_000) return `il y a ${Math.floor(diff / 3_600_000)} h`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export default StyleNotesPanel;
